/* ==========================================================================
   duvelle-carousel.js
   Fleches, amorce automatique et defilement en boucle.

   Fluidite du defilement, trois points qui comptent :
   1. l'accroche passe en "proximity" : avec "mandatory" le navigateur force
      l'arret sur chaque point d'accroche, ce qui donne cette sensation de
      blocage quand on fait defiler au doigt ou au pave tactile ;
   2. le tapis roulant ne se replace qu'une fois le defilement reellement
      termine (evenement scrollend, ou repli sur un delai), jamais pendant que
      le doigt est encore pose ;
   3. la mise a jour des fleches est calee sur requestAnimationFrame au lieu
      de relire la geometrie a chaque evenement de defilement.
   ========================================================================== */
(function () {
  'use strict';

  var EDGE = 24;
  var DURATION = 380;
  var SETTLE = 140;

  function getTrack(root) {
    return root.querySelector('[data-dv-carousel-track]');
  }

  function isLooping(root) {
    return root.getAttribute('data-dv-loop') === 'true' && root.dvLoopActive === true;
  }

  function gapOf(track) {
    var styles = getComputedStyle(track);
    return parseFloat(styles.columnGap || styles.gap) || 0;
  }

  function peekOf(track) {
    var value = parseFloat(getComputedStyle(track).scrollPaddingInlineStart);
    return isNaN(value) ? 0 : value;
  }

  function stepSize(track) {
    var card = track.firstElementChild;
    var width = card ? card.getBoundingClientRect().width : track.clientWidth * 0.8;
    return Math.round(width + gapOf(track));
  }

  function visibleCount(track) {
    var step = stepSize(track);
    if (step <= 0) return 1;
    return Math.max(1, Math.round((track.clientWidth - peekOf(track) * 2) / step));
  }

  function update(root) {
    var track = getTrack(root);
    if (!track) return;

    var max = track.scrollWidth - track.clientWidth;
    root.setAttribute('data-scrollable', max > EDGE ? 'true' : 'false');

    var prev = root.querySelector('[data-dv-carousel-prev]');
    var next = root.querySelector('[data-dv-carousel-next]');

    if (isLooping(root)) {
      if (prev) prev.disabled = false;
      if (next) next.disabled = false;
      return;
    }

    if (prev) prev.disabled = track.scrollLeft <= EDGE;
    if (next) next.disabled = track.scrollLeft >= max - EDGE;
  }

  function scheduleUpdate(root, track) {
    if (track.dvFrame) return;
    track.dvFrame = window.requestAnimationFrame(function () {
      track.dvFrame = 0;
      update(root);
    });
  }

  /* ---------- Tapis roulant ---------- */

  function keepPlaying(card, action) {
    var video = card.querySelector('video');
    var wasPlaying = video && !video.paused && !video.ended;
    var time = video ? video.currentTime : 0;

    action();

    if (video && wasPlaying) {
      try {
        video.currentTime = time;
      } catch (error) {
        /* certaines sources refusent le repositionnement, sans gravite */
      }
      var attempt = video.play();
      if (attempt && typeof attempt.catch === 'function') attempt.catch(function () {});
    }
  }

  function shiftForward(track) {
    var first = track.firstElementChild;
    if (!first) return;
    var amount = first.getBoundingClientRect().width + gapOf(track);
    var snap = track.style.scrollSnapType;
    track.style.scrollSnapType = 'none';

    keepPlaying(first, function () {
      track.appendChild(first);
      track.scrollLeft = track.scrollLeft - amount;
    });

    track.style.scrollSnapType = snap;
  }

  function shiftBackward(track) {
    var last = track.lastElementChild;
    if (!last) return;
    var amount = last.getBoundingClientRect().width + gapOf(track);
    var snap = track.style.scrollSnapType;
    track.style.scrollSnapType = 'none';

    keepPlaying(last, function () {
      track.insertBefore(last, track.firstElementChild);
      track.scrollLeft = track.scrollLeft + amount;
    });

    track.style.scrollSnapType = snap;
  }

  function balance(root, track) {
    if (!isLooping(root) || track.dvAnimating || track.dvBalancing) return;
    /* Jamais pendant que la cliente a encore le doigt ou la souris posee. */
    if (track.dvPointerDown) return;

    var step = stepSize(track);
    if (step <= 0) return;

    track.dvBalancing = true;

    var guard = 0;
    while (guard < 4) {
      var max = track.scrollWidth - track.clientWidth;
      if (track.scrollLeft < step * 0.5) {
        shiftBackward(track);
      } else if (track.scrollLeft > max - step * 0.5) {
        shiftForward(track);
      } else {
        break;
      }
      guard += 1;
    }

    track.dvBalancing = false;
  }

  function setupLoop(root, track) {
    root.dvLoopActive = false;
    if (root.getAttribute('data-dv-loop') !== 'true') return;

    var total = track.children.length;
    var visible = visibleCount(track);

    if (total < visible + 2) return;

    root.dvLoopActive = true;

    track.dvBalancing = true;
    shiftBackward(track);
    track.scrollLeft = Math.max(0, track.scrollLeft - peekOf(track));
    track.dvBalancing = false;

    update(root);
  }

  /* ---------- Deplacement anime ---------- */

  function easeInOutQuad(t) {
    return t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t;
  }

  function slide(root, track, direction) {
    if (track.dvAnimating) return;

    if (isLooping(root)) balance(root, track);

    var max = track.scrollWidth - track.clientWidth;
    var from = track.scrollLeft;
    var target = Math.max(0, Math.min(max, from + direction * stepSize(track)));
    if (Math.abs(target - from) < 1) return;

    var restore = track.style.scrollSnapType;
    track.style.scrollSnapType = 'none';

    function finish() {
      if (!track.dvAnimating) return;
      track.dvAnimating = false;
      track.scrollLeft = target;
      track.style.scrollSnapType = restore;
      window.clearTimeout(track.dvSafety);
      if (isLooping(root)) balance(root, track);
      update(root);
    }

    var reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    track.dvAnimating = true;

    if (reduced) {
      finish();
      return;
    }

    track.dvSafety = window.setTimeout(finish, DURATION + 140);

    var started = null;

    function frame(now) {
      if (!track.dvAnimating) return;
      if (started === null) started = now;

      var progress = Math.min(1, (now - started) / DURATION);
      track.scrollLeft = from + (target - from) * easeInOutQuad(progress);

      if (progress < 1) {
        window.requestAnimationFrame(frame);
      } else {
        finish();
      }
    }

    window.requestAnimationFrame(frame);
  }

  /* ---------- Amorce automatique, une seule fois ---------- */

  function cancelHint(root) {
    root.dvHintCancelled = true;
    window.clearTimeout(root.dvHintTimer);
    if (root.dvHintObserver) {
      root.dvHintObserver.disconnect();
      root.dvHintObserver = null;
    }
  }

  function setupHint(root, track) {
    if (root.getAttribute('data-dv-nudge') !== 'true') return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    if (!('IntersectionObserver' in window)) return;

    var delay = parseInt(root.getAttribute('data-dv-nudge-delay'), 10);
    if (isNaN(delay) || delay < 0) delay = 5000;

    ['pointerdown', 'touchstart', 'wheel', 'keydown'].forEach(function (type) {
      root.addEventListener(
        type,
        function () {
          cancelHint(root);
        },
        { passive: true, once: true }
      );
    });

    root.dvHintObserver = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (root.dvHintCancelled || root.dvHintDone) return;

          if (entry.isIntersecting && entry.intersectionRatio >= 0.4) {
            if (root.dvHintTimer) return;
            root.dvHintTimer = window.setTimeout(function () {
              if (root.dvHintCancelled || root.dvHintDone) return;
              if (track.scrollWidth - track.clientWidth <= EDGE) return;
              root.dvHintDone = true;
              slide(root, track, 1);
              cancelHint(root);
            }, delay);
          } else {
            window.clearTimeout(root.dvHintTimer);
            root.dvHintTimer = null;
          }
        });
      },
      { threshold: [0, 0.4] }
    );

    root.dvHintObserver.observe(root);
  }

  /* ---------- Mise en place ---------- */

  function init(root) {
    if (root.getAttribute('data-dv-init') === 'true') return;
    root.setAttribute('data-dv-init', 'true');

    var track = getTrack(root);
    if (!track) return;

    /* Accroche souple : le defilement libre reste fluide, la piste se recale
       quand elle passe pres d'un point d'accroche au lieu d'y etre forcee. */
    track.style.scrollSnapType = 'x proximity';

    var prev = root.querySelector('[data-dv-carousel-prev]');
    var next = root.querySelector('[data-dv-carousel-next]');

    if (prev) {
      prev.addEventListener('click', function () {
        cancelHint(root);
        slide(root, track, -1);
      });
    }

    if (next) {
      next.addEventListener('click', function () {
        cancelHint(root);
        slide(root, track, 1);
      });
    }

    ['pointerdown', 'touchstart'].forEach(function (type) {
      track.addEventListener(
        type,
        function () {
          track.dvPointerDown = true;
        },
        { passive: true }
      );
    });

    ['pointerup', 'pointercancel', 'touchend', 'touchcancel', 'mouseleave'].forEach(function (type) {
      track.addEventListener(
        type,
        function () {
          track.dvPointerDown = false;
          window.clearTimeout(track.dvSettle);
          track.dvSettle = window.setTimeout(function () {
            balance(root, track);
            update(root);
          }, SETTLE);
        },
        { passive: true }
      );
    });

    function onSettle() {
      if (track.dvAnimating || track.dvPointerDown) return;
      balance(root, track);
      update(root);
    }

    track.addEventListener(
      'scroll',
      function () {
        if (track.dvAnimating) return;
        scheduleUpdate(root, track);

        if (!('onscrollend' in window)) {
          window.clearTimeout(track.dvSettle);
          track.dvSettle = window.setTimeout(onSettle, 180);
        }
      },
      { passive: true }
    );

    if ('onscrollend' in window) {
      track.addEventListener('scrollend', onSettle, { passive: true });
    }

    if ('ResizeObserver' in window) {
      var observer = new ResizeObserver(function () {
        update(root);
      });
      observer.observe(track);
      root.dvResizeObserver = observer;
    }

    window.addEventListener('resize', function () {
      update(root);
    });

    update(root);
    window.setTimeout(function () {
      setupLoop(root, track);
      update(root);
    }, 400);

    setupHint(root, track);
  }

  function boot(scope) {
    (scope || document).querySelectorAll('[data-dv-carousel]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      boot();
    });
  } else {
    boot();
  }

  if (!window.__duvelleCarouselBound) {
    window.__duvelleCarouselBound = true;

    document.addEventListener('shopify:section:load', function (event) {
      boot(event.target);
    });

    document.addEventListener('shopify:section:unload', function (event) {
      event.target.querySelectorAll('[data-dv-carousel]').forEach(function (root) {
        if (root.dvResizeObserver) root.dvResizeObserver.disconnect();
        if (root.dvHintObserver) root.dvHintObserver.disconnect();
        window.clearTimeout(root.dvHintTimer);
      });
    });
  }
})();
